import React from "react";
import DisplayCards from "@/components/ui/display-cards";
import { useSafeWindowManager } from "@/hooks/useSafeWindowManager";
import { useTaskManager } from "@/contexts/TaskManager";
import TaskDetailsWindow from "@/components/windows/TaskDetailsWindow";
import { AllTasksWindow } from "@/components/windows/AllTasksWindow";
import { Button } from "@/components/ui/button";
import { 
  CheckSquare, 
  Clock, 
  AlertTriangle, 
  Target,
  Calendar,
  Users,
  FileText,
  Zap,
  Plus
} from "lucide-react";

export default function TodayTasks() {
  const { windowManager, isAvailable } = useSafeWindowManager();
  const { openWindow } = windowManager;
  const { tasks } = useTaskManager();

  // Get today's tasks (filter out completed tasks)
  const activeTasks = tasks.filter(task => task.progress < 100);
  const todaysTasks = activeTasks.slice(0, 3);
  const totalTasks = activeTasks.length;
  const pendingTasks = activeTasks.filter(task => task.progress === 0).length;

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "high": return <AlertTriangle className="size-4 text-red-300" />;
      case "medium": return <Clock className="size-4 text-amber-300" />;
      case "low": return <CheckSquare className="size-4 text-green-300" />;
      default: return <Target className="size-4 text-blue-300" />;
    }
  };

  const getPriorityColors = (priority: string) => {
    switch (priority) {
      case "high": return {
        iconClassName: "text-red-400",
        titleClassName: "text-red-400"
      };
      case "medium": return {
        iconClassName: "text-amber-400", 
        titleClassName: "text-amber-400"
      };
      case "low": return {
        iconClassName: "text-green-400",
        titleClassName: "text-green-400" 
      };
      default: return {
        iconClassName: "text-blue-400",
        titleClassName: "text-blue-400"
      };
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Fraud Detection": return <Target className="size-4" />;
      case "Meeting": return <Users className="size-4" />;
      case "Development": return <Zap className="size-4" />;
      default: return <FileText className="size-4" />;
    }
  };

  const handleTaskClick = (task: any) => {
    openWindow(
      `task-${task.id}`, 
      `Task: ${task.title}`, 
      <TaskDetailsWindow task={task} />
    );
  };

  const handleViewAllTasks = () => {
    openWindow(
      'all-tasks',
      'All Tasks',
      <AllTasksWindow />
    );
  };

  const taskCards = todaysTasks.map((task, index) => {
    const priorityColors = getPriorityColors(task.priority);
    
    return {
      icon: getPriorityIcon(task.priority),
      title: task.title,
      description: task.description,
      date: task.dueTime,
      ...priorityColors,
      onClick: () => handleTaskClick(task),
      className: index === 0 
        ? "[grid-area:stack] hover:-translate-y-8 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0"
        : index === 1
        ? "[grid-area:stack] translate-x-12 translate-y-8 hover:-translate-y-1 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0"
        : "[grid-area:stack] translate-x-24 translate-y-16 hover:translate-y-8"
    };
  });

  return (
    <div className="w-full">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Today's Tasks</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          {totalTasks} total tasks • {pendingTasks} pending
        </p>
      </div>
      
      {totalTasks === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center bg-card/30 rounded-xl border border-border/20">
          <Calendar className="h-12 w-12 text-muted-foreground/30 mb-3" />
          <h3 className="text-muted-foreground font-medium mb-1">You have no tasks today</h3>
          <p className="text-muted-foreground/70 text-sm mb-4">Great job staying on top of things!</p>
          <button 
            onClick={handleViewAllTasks}
            className="group flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-all duration-200 hover:bg-card/30 rounded-md border border-transparent hover:border-border/40"
          >
            <Calendar className="h-3 w-3 transition-transform group-hover:scale-110" />
            <span>View all tasks</span>
          </button>
        </div>
      ) : (
        <>
          <div className="flex justify-center mb-20">
            <DisplayCards cards={taskCards} />
          </div>
          
          {/* Always show View All button for easy access - positioned well below cards */}
          <div className="flex justify-center mt-12">
            <button 
              onClick={handleViewAllTasks}
              className="group flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-all duration-200 hover:bg-card/30 rounded-lg border border-transparent hover:border-border/40 relative z-10"
            >
              <Calendar className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span className="font-medium">View all tasks</span>
              <span className="text-xs bg-muted/40 px-2 py-0.5 rounded-full">
                {totalTasks}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}